import type { CompanyState } from '../types/storeType';

/**
 * Normalizes role description (business link or login) to the `Role` header value / display.
 */
export function normalizePapelToRoleHeader(sourceRole: string): string {
  const normalizedRole = sourceRole.trim().toUpperCase();
  if (!normalizedRole) return '';
  if (normalizedRole.startsWith('CORRETOR')) return 'CORRETOR';
  if (normalizedRole.startsWith('SUB-ESTIPULANTE')) return 'SUB-ESTIPULANTE';
  if (normalizedRole.startsWith('ESTIPULANTE')) return 'ESTIPULANTE';
  if (normalizedRole.startsWith('BACKOFFICE')) return 'BACKOFFICE';
  return normalizedRole;
}

/**
 * Effective role for the UI:
 * - With a selected link/policy in the company store: prefers `policySelection.papelDescricao`, then `selectedVinculo` role.
 * - With no business selection: no role.
 */
export function resolveEffectiveRole(
  company: Pick<CompanyState, 'selectedCompany' | 'selectedVinculo' | 'sessionRole'>
): string {
  const policySel = company.selectedCompany?.policySelection;
  const selectedVinculo = company.selectedVinculo;
  const hasSelectedPolicy = Boolean(selectedVinculo || policySel?.numeroApolice);

  if (hasSelectedPolicy) {
    const fromPolicy = policySel?.papelDescricao?.trim();
    if (fromPolicy) {
      return normalizePapelToRoleHeader(fromPolicy);
    }
    const papelDesc = selectedVinculo?.representacao?.papel?.descricao?.trim();
    if (papelDesc) {
      return normalizePapelToRoleHeader(papelDesc);
    }
    return '';
  }

  const sessionRole = company.sessionRole?.trim();
  if (sessionRole) {
    return normalizePapelToRoleHeader(sessionRole);
  }
  return '';
}

/**
 * `Role` header for BFF calls: only after a business link/policy is chosen (company store); no `user.role` fallback.
 */
export function resolveBffRoleHeader(
  company: Pick<CompanyState, 'selectedCompany' | 'selectedVinculo' | 'sessionRole'>
): string {
  const policySel = company.selectedCompany?.policySelection;
  const selectedVinculo = company.selectedVinculo;
  const hasSelectedPolicy = Boolean(selectedVinculo || policySel?.numeroApolice);
  if (!hasSelectedPolicy) {
    const sessionRole = company.sessionRole?.trim();
    return sessionRole ? normalizePapelToRoleHeader(sessionRole) : '';
  }

  const fromPolicy = policySel?.papelDescricao?.trim();
  if (fromPolicy) {
    return normalizePapelToRoleHeader(fromPolicy);
  }
  const papelDesc = selectedVinculo?.representacao?.papel?.descricao?.trim();
  if (papelDesc) {
    return normalizePapelToRoleHeader(papelDesc);
  }
  return '';
}
